
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { CampaignModal } from "@/components/campaigns/CampaignModal";
import { CampaignViewModal } from "@/components/campaigns/CampaignViewModal";
import DeleteCampaignDialog from "@/components/campaigns/DeleteCampaignDialog";
import PublishCampaignDialog from "@/components/campaigns/PublishCampaignDialog";
import CampaignResults from "@/components/campaigns/CampaignResults";
import CampaignTabContent from "@/components/campaigns/CampaignTabContent";
import { useCampaigns } from "@/hooks/useCampaigns";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Campaign } from "@/types/campaigns";
import { CampaignStatusUpdater } from "@/components/campaigns/CampaignStatusUpdater";

const Campaigns = () => {
  const [activeTab, setActiveTab] = useState("active");
  const {
    activeCampaigns,
    scheduledCampaigns,
    completedCampaigns,
    draftCampaigns,
    currentCampaign,
    selectedCampaignForDeletion,
    selectedCampaignForPublish,
    selectedCampaignForView,
    isCreateModalOpen,
    isEditModalOpen,
    isDeleteDialogOpen,
    isPublishDialogOpen,
    isResultsSheetOpen,
    isViewModalOpen,
    isLoading,
    setIsCreateModalOpen,
    setIsEditModalOpen,
    setIsDeleteDialogOpen,
    setIsPublishDialogOpen,
    setIsResultsSheetOpen,
    setIsViewModalOpen,
    setSelectedCampaignForDeletion,
    handleCreateCampaign,
    handleEditCampaign,
    handleDeleteCampaign,
    handlePublishCampaign,
    handleDuplicateCampaign,
    handleFinalizeCampaign,
    handleReactivateCampaign,
    openEditModal,
    openPublishDialog,
    openResultsSheet,
    openViewModal
  } = useCampaigns();

  const handleCampaignStatusUpdate = () => {
    // Forçar recarregamento das campanhas após verificação manual
    window.location.reload();
  };

  const handleOpenDeleteDialog = (campaign: any) => {
    // Encontrar a campanha completa baseada no ID
    const allCampaigns = [...activeCampaigns, ...scheduledCampaigns, ...completedCampaigns, ...draftCampaigns];
    const fullCampaign = allCampaigns.find(c => c.id === campaign.id);
    
    if (fullCampaign) {
      setSelectedCampaignForDeletion(fullCampaign);
      setIsDeleteDialogOpen(true);
    } else {
      console.error('❌ Campanha não encontrada para exclusão');
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedCampaignForDeletion) {
      try {
        await handleDeleteCampaign(selectedCampaignForDeletion.id);
        setSelectedCampaignForDeletion(null);
        setIsDeleteDialogOpen(false);
      } catch (error) {
        setSelectedCampaignForDeletion(null);
      }
    } else {
      console.error('❌ Nenhuma campanha selecionada para exclusão');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando campanhas...</div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <header className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Campanhas</h1>
          <p className="text-gray-500">Gerencie suas campanhas promocionais</p>
        </div>
        <div className="flex items-center gap-3">
          <CampaignStatusUpdater onStatusUpdate={handleCampaignStatusUpdate} />
          <Button 
            variant="brand"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <PlusCircle className="h-4 w-4 mr-2" />
            Nova Campanha
          </Button>
        </div>
      </header>

      <Tabs defaultValue="active" onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-4">
          <TabsTrigger value="draft">Rascunhos</TabsTrigger>
          <TabsTrigger value="active">Ativas</TabsTrigger>
          <TabsTrigger value="scheduled">Agendadas</TabsTrigger>
          <TabsTrigger value="completed">Finalizadas</TabsTrigger>
        </TabsList>
        
        <TabsContent value="draft" className="mt-6">
          <CampaignTabContent
            title="Campanhas em Rascunho"
            description="Campanhas que ainda não foram publicadas"
            campaigns={draftCampaigns}
            openEditModal={openEditModal}
            openDeleteDialog={handleOpenDeleteDialog}
            openPublishDialog={openPublishDialog}
            openResultsSheet={openResultsSheet}
            openViewModal={openViewModal}
            onDuplicate={handleDuplicateCampaign}
            onFinalize={handleFinalizeCampaign}
            status="draft"
          />
        </TabsContent>
        
        <TabsContent value="active" className="mt-6">
          <CampaignTabContent
            title="Campanhas Ativas"
            description="Campanhas que estão em andamento no momento"
            campaigns={activeCampaigns}
            openEditModal={openEditModal}
            openDeleteDialog={handleOpenDeleteDialog}
            openPublishDialog={openPublishDialog}
            openResultsSheet={openResultsSheet}
            openViewModal={openViewModal}
            onDuplicate={handleDuplicateCampaign}
            onFinalize={handleFinalizeCampaign}
            status="active"
          />
        </TabsContent>
        
        <TabsContent value="scheduled" className="mt-6">
          <CampaignTabContent
            title="Campanhas Agendadas"
            description="Campanhas programadas para datas futuras"
            campaigns={scheduledCampaigns}
            openEditModal={openEditModal}
            openDeleteDialog={handleOpenDeleteDialog}
            openPublishDialog={openPublishDialog}
            openResultsSheet={openResultsSheet}
            openViewModal={openViewModal}
            onDuplicate={handleDuplicateCampaign}
            onFinalize={handleFinalizeCampaign}
            status="scheduled"
          />
        </TabsContent>
        
        <TabsContent value="completed" className="mt-6">
          <CampaignTabContent
            title="Campanhas Finalizadas"
            description="Campanhas que já foram encerradas"
            campaigns={completedCampaigns}
            openEditModal={openEditModal}
            openDeleteDialog={handleOpenDeleteDialog}
            openPublishDialog={openPublishDialog}
            openResultsSheet={openResultsSheet}
            openViewModal={openViewModal}
            onDuplicate={handleDuplicateCampaign}
            onFinalize={handleFinalizeCampaign}
            onReactivate={handleReactivateCampaign}
            status="completed"
          />
        </TabsContent>
      </Tabs>
      
      {/* Create Campaign Modal */}
      <CampaignModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onSave={handleCreateCampaign}
      />
      
      {/* Edit Campaign Modal */}
      {currentCampaign && (
        <CampaignModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onSave={handleEditCampaign}
          campaign={currentCampaign}
        />
      )}
      
      {/* View Campaign Modal */}
      <CampaignViewModal
        open={isViewModalOpen}
        onOpenChange={setIsViewModalOpen}
        campaign={selectedCampaignForView}
      />
      
      {/* Delete Campaign Dialog */}
      <DeleteCampaignDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleConfirmDelete}
        campaign={selectedCampaignForDeletion}
      />
      
      {/* Publish Campaign Dialog */}
      <PublishCampaignDialog
        open={isPublishDialogOpen}
        onOpenChange={setIsPublishDialogOpen}
        onConfirm={handlePublishCampaign}
        campaign={selectedCampaignForPublish}
      />
      
      {/* Campaign Results Sheet */}
      {currentCampaign && (
        <Sheet open={isResultsSheetOpen} onOpenChange={setIsResultsSheetOpen}>
          <SheetContent side="right" className="w-full md:max-w-[900px] overflow-y-auto">
            <SheetHeader className="mb-6">
              <SheetTitle>Resultados: {currentCampaign.name}</SheetTitle>
              <SheetDescription>
                Período: {new Date(currentCampaign.start_date).toLocaleDateString('pt-BR')} - {new Date(currentCampaign.end_date).toLocaleDateString('pt-BR')}
              </SheetDescription>
            </SheetHeader>
            
            <CampaignResults campaign={currentCampaign} />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default Campaigns;
